import { match, P } from "ts-pattern";
import {
  dateTimeRecordToLocalisedDateTime,
  dateTimeToDateTimeRecord,
  jsDateToDateTime,
  type DateTimeRecord,
} from "@visioni/schedules";
import type { AlarmOverride, AlarmSilence } from "../../schema";
import type { ActiveOverride, AlarmControlView, NextTransition } from "./model";

// `endFromDuration` / `extendEnd` deal in luxon `DateTime`s internally but
// the surrounding code never touches luxon directly. Borrow the return
// type of `jsDateToDateTime` so we get a DateTime-shaped value without a
// direct luxon import. `dateTimeToDateTimeRecord` accepts either a bare
// `DateTime` or a branded `LocalisedDateTime`, so we don't need to track
// the brand through our helper.
type BaseDateTime = ReturnType<typeof jsDateToDateTime>;

// #region Duration

/**
 * The user-selected duration of an arm / disarm / adjust / silence action.
 * Smart constructors (see below) enforce bounds so that constructing a
 * `Duration` whose hours are outside `[1, 24]` or minutes outside `[1, 60]`
 * is impossible.
 */
export type Duration =
  | {
      readonly kind: "preset";
      readonly choice: "15m" | "1h" | "4h" | "until_6am";
    }
  | {
      readonly kind: "custom";
      readonly unit: "hours" | "minutes";
      readonly value: number;
    }
  | { readonly kind: "indefinite" };

const MIN_CUSTOM = 1;
const MAX_CUSTOM_HOURS = 24;
const MAX_CUSTOM_MINUTES = 60;

export const Duration = {
  preset: (choice: Extract<Duration, { kind: "preset" }>["choice"]): Duration => ({ kind: "preset", choice }),
  custom: (unit: "hours" | "minutes", value: number): Duration => {
    const max = unit === "hours" ? MAX_CUSTOM_HOURS : MAX_CUSTOM_MINUTES;
    if (!Number.isInteger(value) || value < MIN_CUSTOM || value > max) {
      throw new Error(
        `Duration.custom: ${unit} value ${String(value)} is out of bounds [${String(MIN_CUSTOM)}, ${String(max)}]`,
      );
    }
    return { kind: "custom", unit, value };
  },
  indefinite: (): Duration => ({ kind: "indefinite" }),
} as const;

// #endregion

// #region AlarmControlCommand

/**
 * Commands emitted by the component. Valid pairs with an {@link AlarmControlView}
 * are enforced by the component (buttons are only offered in variants that
 * accept them) and by {@link interpret}, which returns a `noop` for every
 * invalid pairing.
 */
/**
 * Which thing the user wants to "adjust":
 *
 * - `activeOverride` — push the current override's end-time out (or replace
 *   its indefinite end with a bounded one). Only valid on `inOverride`.
 * - `nextTransition` — create a fresh override that starts at the next
 *   scheduled mode flip and persists for the chosen duration, effectively
 *   delaying or shortening the upcoming schedule change. Valid on `scheduled`
 *   and `inException` (and optionally `inOverride`, though the typical UX
 *   for that state is `activeOverride`).
 *
 * Keeping the two cases behind one command kind means the host UI can emit
 * `adjust` from the same button regardless of the underlying view — the
 * interpreter picks the right mutation from `target` + `view`.
 */
export type AdjustTarget = { readonly kind: "activeOverride" } | { readonly kind: "nextTransition" };

export type AlarmControlCommand =
  | { readonly kind: "arm"; readonly duration: Duration }
  | { readonly kind: "disarm"; readonly duration: Duration }
  | { readonly kind: "adjust"; readonly target: AdjustTarget; readonly extendBy: Duration }
  | { readonly kind: "resume" }
  | { readonly kind: "silence"; readonly minutes: 5 | 15 | 60 }
  | { readonly kind: "extendSilence"; readonly minutes: 5 | 15 | 60 };

// #endregion

// #region MutationRequest

/**
 * The HTTP-layer shape chosen by {@link interpret}. One variant per underlying
 * mutation endpoint; `noop` returns carry a reason string for log/test
 * assertions and are produced whenever an invalid `(view, command)` pair
 * would reach this layer.
 */
export type MutationRequest =
  | { readonly kind: "setOverride"; readonly override: AlarmOverride }
  | { readonly kind: "clearOverride" }
  | { readonly kind: "setSilence"; readonly silence: AlarmSilence }
  | { readonly kind: "noop"; readonly reason: string };

// #endregion

// #region Interpreter

/**
 * Pure translator from `(view, command)` → {@link MutationRequest}.
 *
 * Uses exhaustive `match` on both dimensions. Invalid pairs (e.g. `resume`
 * on a `scheduled` view) collapse to a `noop` with a human-readable reason.
 */
export const interpret = (view: AlarmControlView, command: AlarmControlCommand, now: Date): MutationRequest =>
  match({ view, command })
    .returnType<MutationRequest>()
    // --- arm / disarm ---------------------------------------------------
    .with({ view: { kind: "scheduled" }, command: { kind: "arm" } }, ({ view: v, command: c }) =>
      armRequest("armed", c.duration, v.timezone, now),
    )
    .with({ view: { kind: "scheduled" }, command: { kind: "disarm" } }, ({ view: v, command: c }) =>
      armRequest("disarmed", c.duration, v.timezone, now),
    )
    .with({ view: { kind: "inException" }, command: { kind: "arm" } }, ({ view: v, command: c }) =>
      armRequest("armed", c.duration, v.timezone, now),
    )
    .with({ view: { kind: "inException" }, command: { kind: "disarm" } }, ({ view: v, command: c }) =>
      armRequest("disarmed", c.duration, v.timezone, now),
    )
    // --- adjust / resume -------------------------------------------------
    // `adjust` on an override → extend the existing override window.
    // `adjust` targeting the next scheduled transition → create a new
    // override anchored at that transition's timestamp. The latter is valid
    // in scheduled/inException too (that's how we delay an upcoming flip
    // without an override already in place). If no next transition exists
    // we `noop` with a descriptive reason; the host already hides the button
    // in that case but the interpreter stays honest.
    .with(
      { view: { kind: "inOverride" }, command: { kind: "adjust", target: { kind: "activeOverride" } } },
      ({ view: v, command: c }) => {
        // Indefinite overrides have no end to push forward — treating
        // `extendBy: 1h` as "set end = now + 1h" silently *shortens* the
        // override from forever to a finite hour, which is the opposite of
        // what the user asked for. Bail out as a noop; the UI hides the
        // Adjust button when there's nothing to extend so this is
        // unreachable in practice but the interpreter stays honest.
        const end = v.override.period.end;
        if (end === null) return noop("view=inOverride is indefinite (nothing to extend)", c.kind);
        return adjustRequest({ ...v.override, period: { ...v.override.period, end } }, c.extendBy, v.timezone);
      },
    )
    .with(
      { view: { kind: "inOverride" }, command: { kind: "adjust", target: { kind: "nextTransition" } } },
      ({ view: v, command: c }) =>
        v.nextTransition === null ?
          noop("view=inOverride with no nextTransition", c.kind)
        : adjustNextTransitionRequest(v.nextTransition, c.extendBy, v.timezone),
    )
    .with(
      { view: { kind: "scheduled" }, command: { kind: "adjust", target: { kind: "nextTransition" } } },
      ({ view: v, command: c }) =>
        v.nextTransition === null ?
          noop("view=scheduled with no nextTransition", c.kind)
        : adjustNextTransitionRequest(v.nextTransition, c.extendBy, v.timezone),
    )
    .with(
      { view: { kind: "inException" }, command: { kind: "adjust", target: { kind: "nextTransition" } } },
      ({ view: v, command: c }) =>
        v.nextTransition === null ?
          noop("view=inException with no nextTransition", c.kind)
        : adjustNextTransitionRequest(v.nextTransition, c.extendBy, v.timezone),
    )
    // `applying` carries the configured schedule's next transition so the
    // user can pre-empt it while the previous change is still propagating.
    .with(
      { view: { kind: "applying" }, command: { kind: "adjust", target: { kind: "nextTransition" } } },
      ({ view: v, command: c }) =>
        v.nextTransition === null ?
          noop("view=applying with no nextTransition", c.kind)
        : adjustNextTransitionRequest(v.nextTransition, c.extendBy, v.timezone),
    )
    // `adjust` on scheduled/inException/applying targeting an activeOverride
    // is a contradiction — there is no active override. Emit noop with a
    // specific reason rather than letting the per-view catch-all swallow
    // it (which would return a generic "view=applying" reason that hides
    // the actual problem).
    .with(
      { view: { kind: "scheduled" }, command: { kind: "adjust", target: { kind: "activeOverride" } } },
      ({ command: c }) => noop("view=scheduled has no active override", c.kind),
    )
    .with(
      { view: { kind: "inException" }, command: { kind: "adjust", target: { kind: "activeOverride" } } },
      ({ command: c }) => noop("view=inException has no active override", c.kind),
    )
    .with(
      { view: { kind: "applying" }, command: { kind: "adjust", target: { kind: "activeOverride" } } },
      ({ command: c }) => noop("view=applying has no active override", c.kind),
    )
    .with({ view: { kind: "inOverride" }, command: { kind: "resume" } }, () => ({
      kind: "clearOverride" as const,
    }))
    // --- silence (valid only in activated) ------------------------------
    .with({ view: { kind: "activated" }, command: { kind: "silence" } }, ({ view: v, command: c }) =>
      silenceRequest(v.timezone, c.minutes, null, now),
    )
    .with({ view: { kind: "activated" }, command: { kind: "extendSilence" } }, ({ view: v, command: c }) =>
      silenceRequest(v.timezone, c.minutes, v.silence.kind === "active" ? v.silence.minutesRemaining : null, now),
    )
    // --- every other combination → noop ---------------------------------
    .with({ view: { kind: "loading" }, command: P._ }, ({ command: c }) => noop("view=loading", c.kind))
    .with({ view: { kind: "error" }, command: P._ }, ({ command: c }) => noop("view=error", c.kind))
    // While a command is in flight, ignore all further commands. This is the
    // server-side equivalent of disabling every button: double-taps can't
    // produce a second request, and an Adjust tapped mid-Disarm can't jump
    // the queue.
    .with({ view: { kind: "pending" }, command: P._ }, ({ command: c }) => noop("view=pending", c.kind))
    .with({ view: { kind: "unconfigured" }, command: P._ }, ({ command: c }) => noop("view=unconfigured", c.kind))
    .with({ view: { kind: "applying" }, command: P._ }, ({ command: c }) => noop("view=applying", c.kind))
    .with({ view: { kind: "scheduled" }, command: P._ }, ({ command: c }) => noop("view=scheduled", c.kind))
    .with({ view: { kind: "inException" }, command: P._ }, ({ command: c }) => noop("view=inException", c.kind))
    .with({ view: { kind: "inOverride" }, command: P._ }, ({ command: c }) => noop("view=inOverride", c.kind))
    .with({ view: { kind: "activated" }, command: P._ }, ({ command: c }) => noop("view=activated", c.kind))
    .exhaustive();

// #endregion

// #region Helpers

const noop = (viewDesc: string, commandKind: AlarmControlCommand["kind"]): MutationRequest => ({
  kind: "noop",
  reason: `${commandKind} is not valid when ${viewDesc}`,
});

const armRequest = (action: "armed" | "disarmed", duration: Duration, timezone: string, now: Date): MutationRequest => {
  const start = dateTimeToDateTimeRecord(jsDateToDateTime(now).setZone(timezone));
  const end = endFromDuration(duration, timezone, now);
  return {
    kind: "setOverride",
    override: {
      id: `override-${String(now.getTime())}`,
      reason: action === "armed" ? "Temporary arm" : "Temporary disarm",
      action,
      author: null,
      period: { start, end },
    },
  };
};

/**
 * Create a new override anchored at the next scheduled transition. The
 * override takes the *opposite* mode of whatever the schedule is about to
 * flip to — the user is effectively saying "skip this upcoming change for a
 * while". Example: schedule is about to disarm at 18:00; adjusting by 1h
 * produces an `armed` override from 18:00 to 19:00 so the site stays armed.
 *
 * The start of the override is `nextTransition.at`; the end is `start +
 * extendBy`. `indefinite` durations produce an open-ended override.
 */
const adjustNextTransitionRequest = (next: NextTransition, extendBy: Duration, timezone: string): MutationRequest => {
  const action: "armed" | "disarmed" = next.to === "armed" ? "disarmed" : "armed";
  const start = next.at;
  const end = extendEnd(start, extendBy, timezone);
  return {
    kind: "setOverride",
    override: {
      id: `override-next-${formatRecordId(start)}-${action}`,
      reason: action === "armed" ? "Delay disarm" : "Delay arm",
      action,
      author: null,
      period: { start, end },
    },
  };
};

const formatRecordId = (record: DateTimeRecord): string =>
  `${String(record.year)}${String(record.month).padStart(2, "0")}${String(record.day).padStart(2, "0")}T${String(record.hour).padStart(2, "0")}${String(record.minute).padStart(2, "0")}`;

/**
 * Push an active override's end forward by {@link extendBy}. The
 * {@link existing} parameter requires a *finite* end — indefinite
 * overrides aren't a valid input here. The caller is the
 * `adjust(activeOverride)` interpret branch, which already short-
 * circuits to a noop when `period.end === null`; that contract is
 * encoded in the type so this function can't silently shorten an
 * infinite override into a finite window.
 */
type FiniteOverride = Omit<ActiveOverride, "period"> & {
  readonly period: Omit<ActiveOverride["period"], "end"> & { readonly end: DateTimeRecord };
};

const adjustRequest = (existing: FiniteOverride, extendBy: Duration, timezone: string): MutationRequest => {
  const end = extendEnd(existing.period.end, extendBy, timezone);
  return {
    kind: "setOverride",
    override: {
      id: existing.id,
      reason: `Extended ${existing.action === "armed" ? "arm" : "disarm"}`,
      action: existing.action,
      author: null,
      period: { start: existing.period.start, end },
    },
  };
};

const silenceRequest = (
  timezone: string,
  minutes: 5 | 15 | 60,
  currentRemaining: number | null,
  now: Date,
): MutationRequest => {
  const nowDt = jsDateToDateTime(now).setZone(timezone);
  const start = dateTimeToDateTimeRecord(nowDt);
  const endDt =
    currentRemaining !== null ? nowDt.plus({ minutes: currentRemaining + minutes }) : nowDt.plus({ minutes });
  const end = dateTimeToDateTimeRecord(endDt);
  return {
    kind: "setSilence",
    silence: {
      id: `silence-${String(now.getTime())}`,
      reason:
        currentRemaining !== null ?
          `Extended silence by ${String(minutes)} minutes`
        : `Silenced for ${String(minutes)} minutes`,
      author: null,
      period: { start, end },
    },
  };
};

/**
 * Resolve a {@link Duration} against a base `DateTime`, returning the
 * implied end `DateTime` — or `null` when the duration is indefinite.
 *
 * The two call sites differ only in how they obtain the base: the arm /
 * silence paths anchor on `now`; the adjust-override path anchors on the
 * existing override's end record. Both agree on every other branch
 * (`15m` / `1h` / `4h` / `until_6am` / `custom`). Sharing this helper
 * keeps them in sync when new preset choices are added or preset
 * semantics change.
 */
const applyDurationToBase = (base: BaseDateTime, duration: Duration): BaseDateTime | null =>
  match(duration)
    .returnType<BaseDateTime | null>()
    .with({ kind: "indefinite" }, () => null)
    .with({ kind: "preset" }, (d) =>
      match(d.choice)
        .with("15m", () => base.plus({ minutes: 15 }))
        .with("1h", () => base.plus({ hours: 1 }))
        .with("4h", () => base.plus({ hours: 4 }))
        .with("until_6am", () => {
          // Next 6am: today's 6am if we're before it, tomorrow's otherwise.
          const today6am = base.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });
          return today6am > base ? today6am : today6am.plus({ days: 1 });
        })
        .exhaustive(),
    )
    .with({ kind: "custom" }, (d) =>
      d.unit === "hours" ? base.plus({ hours: d.value }) : base.plus({ minutes: d.value }),
    )
    .exhaustive();

const endFromDuration = (duration: Duration, timezone: string, now: Date): DateTimeRecord | null => {
  const nowDt = jsDateToDateTime(now).setZone(timezone);
  const endDt = applyDurationToBase(nowDt, duration);
  return endDt === null ? null : dateTimeToDateTimeRecord(endDt);
};

const extendEnd = (base: DateTimeRecord, duration: Duration, timezone: string): DateTimeRecord | null => {
  const baseDt = dateTimeRecordToLocalisedDateTime(base, timezone);
  const endDt = applyDurationToBase(baseDt, duration);
  return endDt === null ? null : dateTimeToDateTimeRecord(endDt);
};

// #endregion
